import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import type { ValidationError } from "~/common/types";
import {
  GetProductDocument,
  GetProductsDocument,
  UpdateProductAvailableRolesDocument,
  type UpdateProductAvailableRolesMutation,
  type UpdateProductAvailableRolesMutationVariables,
  UpsertProductDocument,
  type UpsertProductMutation,
  type UpsertProductMutationVariables,
} from "~/features/admin/products/gqls";
import type { ProductEditFormData } from "~/features/admin/products/types";
import { translateError } from "~/lib/errors";

interface UseProductEditOptions {
  onSuccess?: (data: ProductEditFormData) => void;
  onError?: (error: Pick<ValidationError, "fields">) => void;
}

/**
 * Edits a product. As in V1, this runs two mutations in sequence: the product
 * upsert first, then the available-roles update (the roles live behind a
 * separate mutation and are only set from the edit form, never on create).
 */
export function useProductEdit({ onSuccess, onError }: UseProductEditOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [upsertProduct] = useMutation<UpsertProductMutation, UpsertProductMutationVariables>(UpsertProductDocument);
  const [updateAvailableRoles] = useMutation<
    UpdateProductAvailableRolesMutation,
    UpdateProductAvailableRolesMutationVariables
  >(UpdateProductAvailableRolesDocument);

  const submit = useCallback(
    async (formData: ProductEditFormData) => {
      setProcessing(true);
      try {
        const refetchQueries = [GetProductsDocument, { query: GetProductDocument, variables: { id: formData.id } }];
        const res = await upsertProduct({
          variables: {
            input: {
              id: formData.id,
              name: formData.name,
              originHosts: formData.originHosts,
              importDirectWebhookUrl: formData.importDirectWebhookUrl || null,
            },
          },
        });
        // Bail out before touching roles if the upsert was rejected — mirrors
        // V1's `if (!ok) return;` between the two mutations.
        const upsertOk = match(res.data?.cnutUpsertProduct)
          .with({ __typename: "ValidationError" }, (e) => {
            onError?.(e);
            return false;
          })
          .with({ __typename: "Product" }, () => true)
          .otherwise(() => false);
        if (!upsertOk) return;

        await updateAvailableRoles({
          variables: {
            input: { productId: formData.id, roleIds: formData.availableRoleIds },
          },
          refetchQueries,
        });
        toast.success(t("feature.common.updatedSuccessfully", { model: "product" }));
        onSuccess?.(formData);
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [upsertProduct, updateAvailableRoles, t, onSuccess, onError],
  );

  return { submit, processing };
}
